import AnalyticsService from '@emcd-shared/analytics-service'
import { HTTP_STATUS_CODES } from '@/packages/auth-service/common/const'
import { TOAST_DISPATCHERS } from '@/shared/constants/store-constants/toast'
import i18n from '@/app/i18n'

// Cooldown to deduplicate identical business toasts triggered by burst/parallel requests
const TOAST_COOLDOWN_MS = 5_000
const lastToastAt = new Map()

function shouldShowToast(key) {
  const now = Date.now()
  const last = lastToastAt.get(key) || 0
  if (now - last < TOAST_COOLDOWN_MS) return false
  lastToastAt.set(key, now)
  return true
}

/**
 * Application-level error interceptor for business logic
 * Handles: 400 with code '0555' (product not activated), 'kyb-failed' (KYB not passed),
 *          403 with code 'kyb-tier-too-low' (KYB tier below required minimum)
 */
export function businessErrorInterceptor(store) {
  return function (error) {
    const status = error?.response?.status
    const responseData = error?.response?.data
    const errorCode = responseData?.code || responseData?.error?.code || null

    // Handle product not activated: 400 with specific code '0555'
    if (status === HTTP_STATUS_CODES.BAD_REQUEST && errorCode === '0555') {
      AnalyticsService.addBreadcrumb(
        'BusinessInterceptor',
        'productNotActivated'
      )

      if (store) {
        store.dispatch('ui/showProductActivationModal', 'product')
      }

      error.isHandled = true
      return Promise.reject(error)
    }

    // Handle KYB not passed: 400 with code 'kyb-failed'
    // Acts as a safety net — primary gating happens via useKybGuard at submit time.
    if (
      status === HTTP_STATUS_CODES.BAD_REQUEST &&
      errorCode === 'kyb-failed'
    ) {
      AnalyticsService.addBreadcrumb('BusinessInterceptor', 'kybFailed')

      if (store) {
        store.dispatch('account/fetchKybStatus')

        if (!store.state.ui.showProductActivationModal) {
          store.dispatch('ui/showProductActivationModal', 'kyb')
        }
      }

      error.isHandled = true
      return Promise.reject(error)
    }

    // Handle KYB tier below required minimum: 403 with code 'kyb-tier-too-low'
    // User is authenticated; this is a business gate, not an auth failure — show a toast prompting KYB.
    if (
      status === HTTP_STATUS_CODES.FORBIDDEN &&
      errorCode === 'kyb-tier-too-low'
    ) {
      AnalyticsService.addBreadcrumb('BusinessInterceptor', 'kybTierTooLow')

      if (store && shouldShowToast('kyb-tier-too-low')) {
        store.dispatch(
          TOAST_DISPATCHERS.setErrorMessage,
          {
            title: i18n.global.t('KybToast | tierTooLow'),
            message: '',
          },
          { root: true }
        )

        store.dispatch('account/fetchKybStatus')
      }

      error.isHandled = true
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
}
